import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Avatar,
  LinearProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  People,
  WbSunny,
  AccountBalance,
  Assignment,
  Visibility,
  Phone,
  Business,
  PersonAdd,
  LocationOn,
  Money,
  ElectricBolt,
  BatteryChargingFull,
  Schedule,
  CalendarToday,
  AccessTime,
  Flag,
  Warning,
  PendingActions,
  EventBusy,
  Engineering,
  Settings
} from '@mui/icons-material';

// Solar Energy Dashboard Stats
const solarEnergyDashboardStats = [
  {
    title: 'Total Installations',
    value: '456',
    change: '+24.7%',
    trending: 'up',
    icon: WbSunny,
    color: '#1976d2'
  },
  {
    title: 'Active Clients',
    value: '234',
    change: '+18.9%',
    trending: 'up',
    icon: People,
    color: '#388e3c'
  },
  {
    title: 'Energy Generated',
    value: '2.4MW',
    change: '+15.6%',
    trending: 'up',
    icon: ElectricBolt,
    color: '#f57c00'
  },
  {
    title: 'Revenue',
    value: '₹5.2Cr',
    change: '+19.8%',
    trending: 'up',
    icon: Money,
    color: '#7b1fa2'
  },
  {
    title: 'Projects',
    value: '67',
    change: '+12.3%',
    trending: 'up',
    icon: Business,
    color: '#00796b'
  },
  {
    title: 'Maintenance',
    value: '128',
    change: '+8.4%',
    trending: 'up',
    icon: Settings,
    color: '#5d4037'
  },
  {
    title: 'New Leads',
    value: '89',
    change: '+31.2%',
    trending: 'up',
    icon: PersonAdd,
    color: '#303f9f'
  },
  {
    title: 'Consultations',
    value: '156',
    change: '+16.7%',
    trending: 'up',
    icon: LocationOn,
    color: '#e64a19'
  }
];

// Today's Agenda data for Solar Energy
const todaysAgenda = [
  {
    id: 1,
    type: 'installation',
    title: 'Solar Panel Installation - Residential',
    customer: 'Rajesh Kumar',
    time: '10:00 AM',
    location: 'Bungalow #45, Green Park',
    phone: '+91 9876543210',
    status: 'Scheduled'
  },
  {
    id: 2,
    type: 'consultation',
    title: 'Technical Consultation - Commercial',
    customer: 'Tech Industries Ltd',
    time: '02:30 PM',
    location: 'Corporate Office',
    phone: '+91 9876543211',
    status: 'Confirmed'
  },
  {
    id: 3,
    type: 'followup-lead',
    title: 'Lead Follow-up Call',
    customer: 'Priya Sharma',
    time: '11:15 AM',
    location: 'Phone Call',
    phone: '+91 9876543212',
    status: 'Pending'
  },
  {
    id: 4,
    type: 'maintenance',
    title: 'System Maintenance Check',
    customer: 'Amit ',
    time: '04:00 PM',
    location: 'Industrial Complex',
    phone: '+91 9876543213',
    status: 'High Priority'
  },
  {
    id: 5,
    type: 'installation',
    title: 'Battery System Installation',
    customer: 'Vikram Singh',
    time: '09:30 AM',
    location: 'Farmhouse, Highway Road',
    phone: '+91 9876543214',
    status: 'In Progress'
  },
  {
    id: 6,
    type: 'consultation',
    title: 'Energy Audit Assessment',
    customer: 'Green Solutions',
    time: '03:45 PM',
    location: 'Manufacturing Unit',
    phone: '+91 9876543215',
    status: 'Scheduled'
  }
];

// Pending Tasks data for Solar Energy
const pendingTasks = [
  {
    id: 1,
    type: 'pending-installation',
    title: 'Delayed Installation - Rooftop Solar',
    customer: 'Ramesh Gupta',
    dueDate: '2025-10-08',
    priority: 'High',
    phone: '+91 9876543220',
    location: 'Residential Complex',
    daysOverdue: 2
  },
  {
    id: 2,
    type: 'followup',
    title: 'Client Follow-up Call',
    customer: 'Sunita Verma',
    dueDate: '2025-10-09',
    priority: 'Medium',
    phone: '+91 9876543221',
    location: 'Phone Call',
    daysOverdue: 1
  },
  {
    id: 3,
    type: 'maintenance-urgent',
    title: 'Urgent System Maintenance',
    customer: 'Manoj Industries',
    dueDate: '2025-10-07',
    priority: 'Critical',
    phone: '+91 9876543222',
    location: 'Factory Site',
    daysOverdue: 3
  },
  {
    id: 4,
    type: 'pending-installation',
    title: 'Grid Connection Pending',
    customer: 'Kavitha Rao',
    dueDate: '2025-10-09',
    priority: 'Medium',
    phone: '+91 9876543223',
    location: 'Commercial Building',
    daysOverdue: 1
  },
  {
    id: 5,
    type: 'followup',
    title: 'Payment Follow-up Required',
    customer: 'Dinesh Patel',
    dueDate: '2025-10-06',
    priority: 'High',
    phone: '+91 9876543224',
    location: 'Phone Call',
    daysOverdue: 4
  },
  {
    id: 6,
    type: 'maintenance-urgent',
    title: 'System Performance Check',
    customer: 'Neha Sharma',
    dueDate: '2025-10-08',
    priority: 'Medium',
    phone: '+91 9876543225',
    location: 'Solar Farm',
    daysOverdue: 2
  }
];

const recentInstallations = [
  {
    id: 'SOL001',
    customerName: 'Rajesh Kumar',
    capacity: '5 kW',
    amount: 350000,
    status: 'Completed',
    date: '2025-10-08',
    phone: '+91 9876543210'
  },
  {
    id: 'SOL002',
    customerName: 'Priya Sharma',
    capacity: '10 kW',
    amount: 650000,
    status: 'In Progress',
    date: '2025-10-07',
    phone: '+91 9876543211'
  },
  {
    id: 'SOL003',
    customerName: 'Amit Patel',
    capacity: '25 kW',
    amount: 1500000,
    status: 'Installed',
    date: '2025-10-06',
    phone: '+91 9876543212'
  },
  {
    id: 'SOL004',
    customerName: 'Sneha Reddy',
    capacity: '3 kW',
    amount: 225000,
    status: 'Planning',
    date: '2025-10-05',
    phone: '+91 9876543213'
  }
];

const topTechnicians = [
  {
    name: 'Vikram Singh',
    id: 'TECH001',
    installations: 28,
    revenue: 2100000,
    target: 2500000,
    avatar: 'VS'
  },
  {
    name: 'Anita Desai',
    id: 'TECH002',
    installations: 24,
    revenue: 1850000,
    target: 2200000,
    avatar: 'AD'
  },
  {
    name: 'Rohit Gupta',
    id: 'TECH003',
    installations: 22,
    revenue: 1650000,
    target: 2000000,
    avatar: 'RG'
  },
  {
    name: 'Kavita Jain',
    id: 'TECH004',
    installations: 20,
    revenue: 1450000,
    target: 1800000,
    avatar: 'KJ'
  }
];

function SolarEnergyDashboard() {
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed':
      case 'Installed':
        return 'success';
      case 'In Progress':
        return 'info';
      case 'Planning':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getAgendaStatusColor = (status) => {
    switch (status) {
      case 'Confirmed':
      case 'Scheduled':
        return 'success';
      case 'Pending':
        return 'warning';
      case 'High Priority':
        return 'error';
      case 'In Progress':
        return 'info';
      default:
        return 'default';
    }
  };

  const getAgendaIcon = (type) => {
    switch (type) {
      case 'installation':
        return <WbSunny />;
      case 'consultation':
        return <Engineering />;
      case 'followup-lead':
        return <PersonAdd />;
      case 'maintenance':
        return <Settings />;
      default:
        return <Schedule />;
    }
  };

  const getPendingTaskIcon = (type) => {
    switch (type) {
      case 'pending-installation':
        return <EventBusy />;
      case 'followup':
        return <PendingActions />;
      case 'maintenance-urgent':
        return <Settings />;
      default:
        return <Warning />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Critical':
        return 'error';
      case 'High':
        return 'warning';
      case 'Medium':
        return 'info';
      case 'Low':
        return 'success';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Solar Energy Dashboard
        </Typography>
        <Typography variant="body1" color="textSecondary">
          Welcome back! Here's your solar energy business overview for today.
        </Typography>
        <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
          {currentTime.toLocaleString('en-IN', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
          })}
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {solarEnergyDashboardStats.map((stat, index) => {
          const IconComponent = stat.icon;
          return (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card elevation={3}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{ bgcolor: stat.color, mr: 2 }}>
                      <IconComponent />
                    </Avatar>
                    <Box>
                      <Typography variant="h4" component="div" fontWeight="bold">
                        {stat.value}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {stat.title}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {stat.trending === 'up' ? (
                      <TrendingUp sx={{ color: 'success.main', mr: 1 }} />
                    ) : (
                      <TrendingDown sx={{ color: 'error.main', mr: 1 }} />
                    )}
                    <Typography
                      variant="body2"
                      color={stat.trending === 'up' ? 'success.main' : 'error.main'}
                      fontWeight="medium"
                    >
                      {stat.change} from last month
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Grid container spacing={3}>
        {/* Left Side - Recent Installations and Top Technicians */}
        <Grid item xs={12} lg={6}>
          {/* Recent Installations */}
          <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Recent Installations
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Project ID</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Client</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Capacity</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Amount</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentInstallations.map((installation, index) => (
                    <TableRow
                      key={installation.id}
                      sx={{
                        '&:nth-of-type(odd)': { backgroundColor: '#fafafa' },
                        '&:hover': { backgroundColor: '#e3f2fd' }
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'primary.main' }}>
                          {installation.id}
                        </Typography>
                      </TableCell>
                      <TableCell>{installation.customerName}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'success.main' }}>
                          {installation.capacity}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          {formatCurrency(installation.amount)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={installation.status}
                          color={getStatusColor(installation.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="View Details">
                            <IconButton size="small" color="primary">
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Call Client">
                            <IconButton size="small" color="success" href={`tel:${installation.phone}`}>
                              <Phone />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Top Technicians */}
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Top Performing Technicians
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {topTechnicians.map((technician, index) => {
                const progressPercentage = (technician.revenue / technician.target) * 100;
                return (
                  <Box
                    key={technician.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      p: 2,
                      border: '1px solid #e0e0e0',
                      borderRadius: 2,
                      backgroundColor: '#fafafa',
                      '&:hover': {
                        backgroundColor: '#f0f7ff',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                      }
                    }}
                  >
                    <Box sx={{ position: 'relative', mr: 2 }}>
                      <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
                        {technician.avatar}
                      </Avatar>
                      <Box
                        sx={{
                          position: 'absolute',
                          top: -4,
                          right: -4,
                          backgroundColor: progressPercentage >= 100 ? '#4caf50' : progressPercentage >= 75 ? '#ff9800' : '#2196f3',
                          color: 'white',
                          borderRadius: '50%',
                          width: 20,
                          height: 20,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.7rem',
                          fontWeight: 'bold'
                        }}
                      >
                        {index + 1}
                      </Box>
                    </Box>

                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 0.5 }}>
                        {technician.name}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <WbSunny sx={{ fontSize: 14 }} />
                          {technician.installations} installations
                        </Typography>
                        <Typography variant="caption" color="primary.main" sx={{ fontWeight: 'bold' }}>
                          {technician.id}
                        </Typography>
                      </Box>

                      <Box sx={{ mb: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="caption" color="textSecondary">
                            Target: {formatCurrency(technician.target)}
                          </Typography>
                          <Typography variant="caption" fontWeight="bold" color={progressPercentage >= 100 ? 'success.main' : 'primary.main'}>
                            {progressPercentage.toFixed(1)}%
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(progressPercentage, 100)}
                          sx={{
                            height: 6,
                            borderRadius: 3,
                            backgroundColor: '#e0e0e0',
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: progressPercentage >= 100 ? '#4caf50' : progressPercentage >= 75 ? '#ff9800' : '#2196f3'
                            }
                          }}
                        />
                      </Box>
                    </Box>

                    <Box sx={{ textAlign: 'right', ml: 2 }}>
                      <Typography variant="h6" fontWeight="bold" color="success.main" sx={{ mb: 0.5 }}>
                        {formatCurrency(technician.revenue)}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Revenue
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Paper>
        </Grid>

        {/* Right Side - Today's Agenda and Pending Tasks */}
        <Grid item xs={12} lg={6}>
          {/* Today's Agenda */}
          <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Schedule /> Today's Agenda
            </Typography>
            <Typography variant="body2" color="primary" sx={{ mb: 3, fontWeight: 'bold' }}>
              {todaysAgenda.length} tasks scheduled for today
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {todaysAgenda.map((item) => (
                <Box
                  key={item.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 2,
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    backgroundColor: '#fafafa',
                    '&:hover': {
                      backgroundColor: '#f0f7ff',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: getAgendaStatusColor(item.status) === 'success' ? '#4caf50' :
                        getAgendaStatusColor(item.status) === 'warning' ? '#ff9800' :
                          getAgendaStatusColor(item.status) === 'error' ? '#f44336' : '#2196f3',
                      mr: 2,
                      width: 36,
                      height: 36
                    }}
                  >
                    {getAgendaIcon(item.type)}
                  </Avatar>

                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
                      {item.title}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography variant="caption" color="textSecondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <People sx={{ fontSize: 14 }} />
                        {item.customer}
                      </Typography>
                      <Typography variant="caption" color="textSecondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <AccessTime sx={{ fontSize: 14 }} />
                        {item.time}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={item.status}
                      color={getAgendaStatusColor(item.status)}
                      size="small"
                      sx={{ fontSize: '0.7rem', height: 24 }}
                    />
                    <IconButton
                      size="small"
                      color="success"
                      href={`tel:${item.phone}`}
                      sx={{
                        backgroundColor: '#e8f5e8',
                        '&:hover': { backgroundColor: '#4caf50', color: 'white' }
                      }}
                    >
                      <Phone sx={{ fontSize: 16 }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="primary"
                      sx={{
                        backgroundColor: '#e3f2fd',
                        '&:hover': { backgroundColor: '#2196f3', color: 'white' }
                      }}
                    >
                      <Flag sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                </Box>
              ))}
            </Box>
          </Paper>

          {/* Pending Tasks */}
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Warning color="warning" /> Pending Tasks
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Pending Installations */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  p: 2,
                  border: '1px solid #ff9800',
                  borderRadius: 2,
                  backgroundColor: '#fff8e1',
                  '&:hover': {
                    backgroundColor: '#ffecb3',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: '#ff9800',
                    mr: 2,
                    width: 40,
                    height: 40
                  }}
                >
                  <EventBusy />
                </Avatar>

                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 0.5 }}>
                    Pending Installations
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Solar installations that need completion
                  </Typography>
                </Box>

                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="h4" fontWeight="bold" color="warning.main">
                    {pendingTasks.filter(task => task.type === 'pending-installation').length}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Tasks
                  </Typography>
                </Box>
              </Box>

              {/* Follow-ups */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  p: 2,
                  border: '1px solid #2196f3',
                  borderRadius: 2,
                  backgroundColor: '#e3f2fd',
                  '&:hover': {
                    backgroundColor: '#bbdefb',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: '#2196f3',
                    mr: 2,
                    width: 40,
                    height: 40
                  }}
                >
                  <PendingActions />
                </Avatar>

                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 0.5 }}>
                    Follow-ups
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Client follow-up calls and meetings
                  </Typography>
                </Box>

                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="h4" fontWeight="bold" color="primary.main">
                    {pendingTasks.filter(task => task.type === 'followup').length}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Tasks
                  </Typography>
                </Box>
              </Box>

              {/* Urgent Maintenance */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  p: 2,
                  border: '1px solid #f44336',
                  borderRadius: 2,
                  backgroundColor: '#ffebee',
                  '&:hover': {
                    backgroundColor: '#ffcdd2',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: '#f44336',
                    mr: 2,
                    width: 40,
                    height: 40
                  }}
                >
                  <Settings />
                </Avatar>

                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 0.5 }}>
                    Urgent Maintenance
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Critical system maintenance required
                  </Typography>
                </Box>

                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="h4" fontWeight="bold" color="error.main">
                    {pendingTasks.filter(task => task.type === 'maintenance-urgent').length}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Tasks
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Total Summary */}
            <Box sx={{ mt: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 2, textAlign: 'center' }}>
              <Typography variant="body1" color="warning.main" sx={{ fontWeight: 'bold' }}>
                Total {pendingTasks.length} overdue tasks requiring immediate attention
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default SolarEnergyDashboard;